import React, { lazy, Suspense, useMemo, useState } from "react";
import {
  useUserDataFlattened,
  useUserStore,
} from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import { ROUTES } from "@/types/routes";
import {
  useActivityPopupPriority,
  useIsActivityQualificationAutoEnabled,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import { useHomeDisplayStore } from "@/modules/homeDisplay/stores";
import type { FirstRechargePopupResult } from "./useFirstRechargePopup.types";
import {
  qualifiesLegacyFirstRechargePopup,
  hideLegacyFirstRechargeForStaged,
} from "./firstRechargeFloatingHelpers";

const FirstRechargeBonus = lazy(() =>
  import("../components/FirstRechargeBonus/FirstRechargeBonus").then(m => ({
    default: m.FirstRechargeBonus,
  }))
);

const FirstRechargeBonusContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <FirstRechargeBonus visible={visible} onClose={handleClose} />
    </Suspense>
  );
};

const FIRST_RECHARGE_CONFIG = POPUP_CONFIGS.FIRST_RECHARGE;

export const useFirstRechargePopup = (): FirstRechargePopupResult => {
  const { data: userInfo } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();
  // firstRechargeFlag 的权威值来自 user_realtime；已登录用户在 realtime 未加载前，合并数据
  // 读到的是静态 user_info 里可能陈旧的 false，会让已首充用户在窗口期误弹，故等 realtime 就绪。
  // 游客(无 userId)路径 loadGuestInfo 从不写 realtime，不能用它当闸，否则游客首充弹窗被永久压制。
  const isRealtimeReady = useUserStore(
    s => s.realtime !== null || !s.userInfo?.userId
  );
  const snapshot = useHomeDisplayStore(
    s => s.data?.firstRechargeStaged ?? null
  );
  const hideLegacyFirstRecharge = hideLegacyFirstRechargeForStaged(snapshot);

  const priority = useActivityPopupPriority(
    ActivityType.FIRST_DEPOSIT,
    FIRST_RECHARGE_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );
  const isAutoEnabled = useIsActivityQualificationAutoEnabled(
    ActivityType.FIRST_DEPOSIT
  );

  const frbEndAt = useMemo(() => {
    if (typeof window === "undefined") return Date.now() + 60 * 60 * 1000;

    const now = Date.now();
    const key = `rechargeEndAt:${userInfo?.userId ?? "guest"}`;
    const savedRaw = localStorage.getItem(key);
    const saved = savedRaw ? Number(savedRaw) : 0;

    if (saved && saved > now) return saved;

    const newEndAt = now + 60 * 60 * 1000;
    localStorage.setItem(key, String(newEndAt));
    return newEndAt;
  }, [userInfo?.userId]);

  const shouldShow =
    isRealtimeReady &&
    !hideLegacyFirstRecharge &&
    qualifiesLegacyFirstRechargePopup(userInfo) &&
    Date.now() < frbEndAt;

  const { trigger } = useQueuedPopup({
    popupId: FIRST_RECHARGE_CONFIG.id,
    priority,
    shouldShow: () =>
      isConfigReady &&
      isRealtimeReady &&
      !hideLegacyFirstRecharge &&
      qualifiesLegacyFirstRechargePopup(userInfo) &&
      Date.now() < frbEndAt,
    renderContent: onComplete => (
      <FirstRechargeBonusContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: true,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/"],
    dependencies: [
      isConfigReady,
      isRealtimeReady,
      hideLegacyFirstRecharge,
      snapshot?.firstDepositMode,
      snapshot?.showStagedUi,
      userInfo?.firstRechargeRuleAmount,
      userInfo?.firstRechargeFlag,
      userInfo?.firstRechargePopupShowPercent,
      userInfo?.firstRechargeRulePromoRatio,
    ],
    autoTrigger: isAutoEnabled,
    userBinding: true,
    getUserId: () => userInfo?.userId,
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const remainingMs = Math.max(0, frbEndAt - Date.now());

  return {
    shouldShow,
    trigger,
    remainingMs,
  };
};
